import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DndContext,
  DragEndEvent,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import toast from "react-hot-toast";
import { Toaster } from "react-hot-toast";
import {
  getCustomerConfirmations,
  updateConfirmationStatus,
  type KanbanCard,
  type CustomerConfirmation,
} from "../services/whatsapp/whatsappService";
import { decodeTokenService } from "../services/decodeTokenService";
import { handleApi } from "../utils/handleApi";
import KanbanColumn from "./ChatPage/components/KanbanColumn";
import Loader from "../components/common/Loader";

const COLUMNS = [
  { id: "awaiting", title: "En espera", color: "bg-amber-400", statuses: ["pending", "awaiting_response", "send_failed"] },
  { id: "confirmed", title: "Confirmado", color: "bg-emerald-400", statuses: ["confirmed"] },
  { id: "rejected", title: "Rechazado", color: "bg-red-400", statuses: ["rejected", "canceled"] },
  { id: "expired", title: "Expirado", color: "bg-gray-400", statuses: ["expired"] },
];

const STATUS_MAP: Record<string, string> = {
  awaiting: "awaiting_response",
  confirmed: "confirmed",
  rejected: "rejected",
  expired: "expired",
};

function confirmationToCard(conf: CustomerConfirmation): KanbanCard {
  const products = conf.order_data?.products || [];
  return {
    id: conf.id,
    order_id: conf.order_id,
    order_hash_id: conf.order_hash_id,
    status: conf.status,
    customer_name: conf.customer_name,
    customer_phone: conf.customer_phone,
    ai_enabled: conf.ai_enabled,
    created_at: conf.created_at,
    total: conf.order_data?.totalValueWithDiscount || conf.order_data?.totalValue || 0,
    product_name: products[0]?.name || "",
  };
}

function getColumnId(status: string): string {
  for (const col of COLUMNS) {
    if (col.statuses.includes(status)) return col.id;
  }
  return "awaiting";
}

const CustomerKanbanPage = () => {
  const { phone } = useParams<{ phone: string }>();
  const [columns, setColumns] = useState<Record<string, KanbanCard[]>>({});
  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    const load = async () => {
      const ownerId = decodeTokenService.getUserIdFromToken();
      if (!ownerId || !phone) return;

      const { res, error } = await handleApi(getCustomerConfirmations(phone, ownerId), false);
      if (error || !res) {
        setLoading(false);
        return;
      }

      const grouped: Record<string, KanbanCard[]> = {
        awaiting: [],
        confirmed: [],
        rejected: [],
        expired: [],
      };

      res.forEach((conf: CustomerConfirmation) => {
        const card = confirmationToCard(conf);
        const colId = getColumnId(conf.status);
        grouped[colId].push(card);
        if (!customerName && conf.customer_name) {
          setCustomerName(conf.customer_name);
        }
      });

      setColumns(grouped);
      setLoading(false);
    };
    load();
  }, [phone]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const cardId = active.id as number;
    const targetColId = over.id as string;

    // Find which column the card is currently in
    let sourceColId = "";
    let card: KanbanCard | undefined;
    for (const [colId, cards] of Object.entries(columns)) {
      const found = cards.find((c) => c.id === cardId);
      if (found) {
        sourceColId = colId;
        card = found;
        break;
      }
    }

    if (!card || sourceColId === targetColId) return;

    // Check AI
    if (card.ai_enabled) {
      toast.dismiss();
      toast.error("Desactiva la IA desde el chat para mover este pedido.");
      return;
    }

    const targetStatus = STATUS_MAP[targetColId];
    if (!targetStatus) return;

    // Optimistic update
    const prevColumns = { ...columns };
    setColumns((prev) => {
      const source = prev[sourceColId].filter((c) => c.id !== cardId);
      const target = [...prev[targetColId], { ...card!, status: targetStatus as any }];
      return { ...prev, [sourceColId]: source, [targetColId]: target };
    });

    // API call
    const { error } = await handleApi(updateConfirmationStatus(cardId, targetStatus));
    toast.dismiss();
    if (error) {
      setColumns(prevColumns); // Revert
      toast.error("Error al cambiar el estado");
    } else {
      toast.success(`Pedido #${card.order_id} movido a ${COLUMNS.find((c) => c.id === targetColId)?.title}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA] p-8 font-['Plus_Jakarta_Sans']">
      <Toaster position="top-right" toastOptions={{ duration: 2000 }} containerStyle={{ top: 20 }} />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Pedidos de {customerName || phone}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {phone} — Arrastra los pedidos para cambiar su estado
        </p>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => (
            <KanbanColumn
              key={col.id}
              id={col.id}
              title={col.title}
              cards={columns[col.id] || []}
              color={col.color}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
};

export default CustomerKanbanPage;
